// Login/session UI helpers.
//
// The backend owns auth: the browser never reads or stores tokens. We only
// flip overlay visibility and forward form submissions to the API client.

import { getSession, login, logout, UnauthorizedError } from "./api-client.js";
import { els } from "./dom.js";
import { logDebug } from "./debug.js";
import { t } from "./i18n.js";

let currentSpace = null;

export function getCurrentSpace() {
  return currentSpace;
}

export function showLogin({ message } = {}) {
  if (els.loginOverlay) {
    els.loginOverlay.hidden = false;
    els.loginOverlay.dataset.state = "visible";
  }
  if (els.loginError) {
    if (message) {
      els.loginError.textContent = message;
      els.loginError.hidden = false;
    } else {
      els.loginError.textContent = "";
      els.loginError.hidden = true;
    }
  }
  // Reset focus to the space input so keyboard users can retry quickly.
  if (els.loginSpace) {
    try {
      els.loginSpace.focus();
    } catch (_) {}
  }
  if (els.authStatus) els.authStatus.hidden = true;
}

export function hideLogin() {
  if (els.loginOverlay) {
    els.loginOverlay.hidden = true;
    els.loginOverlay.dataset.state = "hidden";
  }
  if (els.loginError) {
    els.loginError.textContent = "";
    els.loginError.hidden = true;
  }
}

export function showAuthStatus(space) {
  currentSpace = space || null;
  if (els.authStatus) els.authStatus.hidden = !space;
  if (els.authSpace) els.authSpace.textContent = space || "";
}

export async function attemptLogin({ space, code }) {
  // Trim on the client for UX only; the backend re-normalizes authoritatively.
  const cleanSpace = String(space || "").trim();
  const cleanCode = String(code || "").trim();
  if (!cleanSpace || !cleanCode) {
    throw new Error(t("auth.missing_fields"));
  }
  const result = await login({ space: cleanSpace, code: cleanCode });
  showAuthStatus(result.space);
  hideLogin();
  return result;
}

export async function attemptLogout() {
  try {
    await logout();
  } catch (e) {
    // We still show login: the cookie was either cleared or invalid.
    logDebug("browser", "logout_error", { message: e.message || String(e) }, "warn");
  }
  currentSpace = null;
  showAuthStatus(null);
  showLogin();
}

export async function loadCurrentSession() {
  try {
    const session = await getSession();
    showAuthStatus(session.space);
    hideLogin();
    return session;
  } catch (e) {
    if (e instanceof UnauthorizedError) {
      showAuthStatus(null);
      showLogin();
      return null;
    }
    throw e;
  }
}
